import {
	ChangeDetectionStrategy,
	Component,
	Input,
	OnInit,
	Signal,
} from '@angular/core';
import { MatterFileTypeStrings } from '@core/util/file';
import { BrokerageType, MatterFileType } from '@models/matter';
import { MatterDocumentType, MatterFile } from '@models/matter-file';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import { Store } from '@ngxs/store';
import {
	AnalyticsEvent,
	AnalyticsService,
} from '@services/analytics/analytics.service';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { MatterUploadedFilesDispatch } from '@state/matter/uploaded-files/matter-uploaded-files.dispatch';
import { MatterUploadedFilesSelectors } from '@state/matter/uploaded-files/matter-uploaded-files.selectors';
import { BehaviorSubject, Observable } from 'rxjs';
import { filter, map, shareReplay, switchMap, take } from 'rxjs/operators';
import { TestHooks } from './commission-statement-file-handler.hooks';
import { SITE_SECTION } from '@state/navigation/page.model';
import { ActivatedRoute } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';

@UntilDestroy()
@Component({
	selector: 'app-commission-statement-file-handler',
	templateUrl: './commission-statement-file-handler.component.html',
	styleUrls: ['./commission-statement-file-handler.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
})
export class CommissionStatementFileHandlerComponent implements OnInit {
	testHooks = TestHooks;
	public commissionStatement$: Observable<MatterFile>;
	public documentName$: Observable<string>;
	public documentType: Signal<MatterDocumentType>;
	public isInMatterCreation: boolean;

	private selectedBrokerageType$: BehaviorSubject<BrokerageType> =
		new BehaviorSubject(BrokerageType.SELLER_BROKERAGE);
	@Input() set selectedBrokerageType(brokerageType: BrokerageType) {
		this.selectedBrokerageType$.next(brokerageType);
	}

	public matterId$ = this.store.select(SelectedMatter.id);
	@SelectSnapshot(SelectedMatter.matterFileType) fileType: MatterFileType;
	public sellerCommissionStatement$ = this.store.select(
		MatterUploadedFilesSelectors.getLatestSellerCommissionStatement,
	);
	public buyerCommissionStatement$ = this.store.select(
		MatterUploadedFilesSelectors.getLatestBuyerCommissionStatement,
	);
	public runMatterDocumentAiScraping = toSignal(
		this.store.select(SelectedMatter.runMatterDocumentAiScrapings),
	);

	constructor(
		private matterUploadedFilesDispatch: MatterUploadedFilesDispatch,
		private analytics: AnalyticsService,
		private route: ActivatedRoute,
		private store: Store,
	) {
		this.documentType = toSignal(
			this.selectedBrokerageType$.pipe(
				untilDestroyed(this),
				filter((x) => !!x),
				map((x) => {
					if (x === BrokerageType.SELLER_BROKERAGE)
						return MatterDocumentType.SELLER_COMMISSION_STATEMENT;
					if (x === BrokerageType.BUYER_BROKERAGE)
						return MatterDocumentType.BUYER_COMMISSION_STATEMENT;
				}),
			),
		);
	}

	ngOnInit(): void {
		this.commissionStatement$ = this.selectedBrokerageType$.pipe(
			untilDestroyed(this),
			filter((x) => !!x),
			switchMap((x) => {
				if (x === BrokerageType.SELLER_BROKERAGE)
					return this.sellerCommissionStatement$;
				if (x === BrokerageType.BUYER_BROKERAGE)
					return this.buyerCommissionStatement$;
			}),
			shareReplay(1),
		);

		this.documentName$ = this.selectedBrokerageType$.pipe(
			untilDestroyed(this),
			filter((x) => !!x),
			map((x) => {
				if (x === BrokerageType.SELLER_BROKERAGE)
					return MatterFileTypeStrings[
						MatterDocumentType.SELLER_COMMISSION_STATEMENT
					];
				if (x === BrokerageType.BUYER_BROKERAGE)
					return MatterFileTypeStrings[
						MatterDocumentType.BUYER_COMMISSION_STATEMENT
					];
			}),
		);

		this.route.data.pipe(untilDestroyed(this)).subscribe((routeData) => {
			this.isInMatterCreation =
				routeData.section === SITE_SECTION.MatterCreation;
		});
	}

	deleteCommissionStatement() {
		this.commissionStatement$
			.pipe(untilDestroyed(this), take(1))
			.subscribe((commissionStatement) => {
				const commStatementId = commissionStatement?.id;

				if (!commStatementId) return;

				this.matterUploadedFilesDispatch.Delete(commStatementId);
			});
	}

	onCommissionStatementUpload() {
		if (
			this.fileType === MatterFileType.SALE &&
			this.selectedBrokerageType$.getValue() === BrokerageType.BUYER_BROKERAGE
		) {
			this.analytics.logEvent(
				AnalyticsEvent.SALE_FILE_COMMISSIONS_BUYER_BROKERAGE_COMMISSION_STATEMENT_UPLOAD,
			);
		}

		if (this.isInMatterCreation) {
			this.analytics.logEvent(
				this.runMatterDocumentAiScraping()?.[this.documentType()]
					? AnalyticsEvent.APS_ONBOARDING_COMMISSION_AI_ON
					: AnalyticsEvent.APS_ONBOARDING_COMMISSION_AI_OFF,
			);
		} else {
			this.analytics.logEvent(
				this.runMatterDocumentAiScraping()?.[this.documentType()]
					? AnalyticsEvent.APS_COMMISSIONS_AI_ON
					: AnalyticsEvent.APS_COMMISSIONS_AI_OFF,
			);
		}
	}
}
